import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Button from "../../../components/Button";
import fileDownload from "js-file-download";
import { API_EXPORT } from "../../../helpers/api";

import { getInvoiceCollateralReportRequested } from "../../../redux/actions/collaterals";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import { Input } from "../../../components/Inputs";
import moment from "moment";
import { convertNumberToFrontend } from "../../../helpers/convert";
import { setNotification } from "../../../helpers/notification";

export default function InvoiceColletarelReport({ t }) {
  const dispatch = useDispatch();

  const { organizationId, organizationName } = useSelector(
    (state) => state.authorization.user.organization
  );

  const { items: invoiceReport, total } = useSelector(
    (state) => state.collaterals.invoiceReport
  );

  const [activePage, setActivePage] = useState(0);
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const [startDate, setStartDate] = useState(null);

  const [endDate, setEndDate] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [downloadFilter, setDownloadFilter] = useState({
    startTerm: startDate
      ? moment(startDate).format("YYYYMM")
      : moment(new Date()).format("YYYYMM"),
    endTerm: endDate ? moment(endDate).format("YYYYMM") : null,
    organizationId: organizationId ?? null,
  });
  const InvoiceColletarelReportColumns = [
    {
      name: t("pages.default.InvoiceColletarelReport.table.billingPeriod"),
      selector: "invoiceTerm",
      sortable: true,
      cell: (row) => row?.invoiceTerm ?? "-",
    },
    {
      name: t("pages.default.InvoiceColletarelReport.table.etsoCode"),
      selector: "etsoCode",
      sortable: true,
      cell: (row) => row?.etsoCode ?? "-",
    },
    {
      name: t("pages.default.InvoiceColletarelReport.table.organizationName"),
      selector: "organizationName",
      sortable: true,
      cell: (row) => row?.organizationName ?? "-",
    },
    {
      name: t("pages.default.InvoiceColletarelReport.table.requiredCollateral"),
      selector: "requiredAssurance",
      sortable: true,
      cell: (row) =>
        convertNumberToFrontend({ num: row?.requiredAssurance, digit: 2 }) ||
        "-",
    },
    {
      name: t("pages.default.InvoiceColletarelReport.table.currentCollateral"),
      selector: "currentAssurance",
      sortable: true,
      cell: (row) =>
        convertNumberToFrontend({ num: row?.currentAssurance, digit: 2 }) ||
        "-",
    },
    {
      name: t("pages.default.InvoiceColletarelReport.table.invoicerecivable"),
      selector: "invoiceCreditIncludingWat",
      sortable: true,
      cell: (row) =>
        convertNumberToFrontend({
          num: row?.invoiceCreditIncludingWat,
          digit: 2,
        }) || "-",
    },
    {
      name: t("pages.default.InvoiceColletarelReport.table.invoiceDebit"),
      selector: "invoiceDebtIncludingWat",
      sortable: true,
      cell: (row) =>
        convertNumberToFrontend({
          num: row?.invoiceDebtIncludingWat,
          digit: 2,
        }) || "-",
    },

    {
      name: t("pages.default.InvoiceColletarelReport.table.exactInvoiceAmount"),
      selector: "subtractionOfDebtFromCredit",
      sortable: true,
      cell: (row) =>
        convertNumberToFrontend({
          num: row?.subtractionOfDebtFromCredit,
          digit: 2,
        }) || "-",
    },
    {
      name: t("pages.default.InvoiceColletarelReport.table.invoiceDueDate"),
      selector: "invoicePaymentDueDate",
      sortable: true,
      cell: (row) => row?.invoicePaymentDueDate ?? "-",
    },
    {
      name: t(
        "pages.default.InvoiceColletarelReport.table.currentCollateralUpdateDate"
      ),
      selector: "currentAssuranceUpdateDate",
      sortable: true,
      cell: (row) =>
        moment(row?.currentAssuranceUpdateDate).isValid()
          ? moment(row?.currentAssuranceUpdateDate).format(
              "DD.MM.YYYY HH:mm:ss"
            )
          : "-",
    },
  ];

  useEffect(() => {
    dispatch(
      getInvoiceCollateralReportRequested({
        startTerm: moment(new Date()).format("YYYYMM"),
        endTerm: endDate ?? null,
        organizationId: organizationId ?? null,
      })
    );
    setStartDate(startDate ? startDate : moment(new Date()).format("YYYYMM"));
    setEndDate(endDate ? endDate : null);
  }, []);

  const filter = () => {
    setStartDate(startDate ? startDate : moment(new Date()).format("YYYYMM"));
    setEndDate(endDate ? endDate : null);

    let params = {
      startTerm: startDate
        ? moment(startDate).format("YYYYMM")
        : moment(new Date()).format("YYYYMM"),
      endTerm: endDate ? moment(endDate).format("YYYYMM") : null,
      organizationId: organizationId ?? null,
    };
    dispatch(getInvoiceCollateralReportRequested({ ...params }));
    setDownloadFilter({
      ...params,
      lang: window.localStorage.i18nextLng,
    });
  };

  const handleClick = (activePage, selector, direction) => {
    //console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    let params = {
      startTerm: startDate
        ? moment(startDate).format("YYYYMM")
        : moment(new Date()).format("YYYYMM"),
      endTerm: endDate ? moment(endDate).format("YYYYMM") : null,
      organizationId: organizationId ?? null,
    };
    dispatch(
      getInvoiceCollateralReportRequested({
        ...params,
        page: activePage,
        selector: selector,
        direction: direction,
      })
    );
  };

  const downloadColletarelReport = async () => {
    setExportLoading(true);

    let params = {
      startTerm: startDate
        ? moment(startDate).format("YYYYMM")
        : moment(new Date()).format("YYYYMM"),
      endTerm: endDate
        ? moment(endDate).format("YYYYMM")
        : moment(new Date()).format("YYYYMM"),
      organizationId: organizationId ?? null,
    };

    setDownloadFilter({
      ...params,
      lang: window.localStorage.i18nextLng,
    });

    try {
      const res = await API_EXPORT("invoice-assurance-report/export/excel", {
        ...downloadFilter,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setExportLoading(false);
    }
  };

  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.InvoiceColletarelReport.title")}
      </h5>

      <div className="km__p--card">
        <div className="km__p--market-results-contracts-header">
          <p className="km__u--text_title">
            {t("pages.default.InvoiceColletarelReport.table.title")}
          </p>
          <div className="km__p--market-results-contracts-header-filter">
            <p className="km__p--market-results-contracts-header-filter-name">
              {`${t(
                "pages.default.IntraSeanceCollateral.organizationName"
              )} : ${organizationName}`}
            </p>
            <Input
              type="month"
              placeholder={t("pages.default.redemption.inputs.startPeriod")}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={new Date(moment(endDate).format("MM-DD-YYYY"))}
              id="ba-history-endDate"
            />
            <Input
              type="month"
              placeholder={t("pages.default.redemption.inputs.endPeriod")}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={new Date(moment(startDate).format("MM-DD-YYYY"))}
              id="ba-history-endDate"
            />
            <Button label={t("ui.default.filter")} onClick={filter} />
            <Button
              type="secondary"
              label={t("ui.default.download")}
              suffixIcon="download"
              onClick={downloadColletarelReport}
              disabled={exportLoading}
              id="c-download"
            />
          </div>
        </div>

        <DataTableServerSideRendering
          columns={InvoiceColletarelReportColumns}
          data={invoiceReport}
          rowKey="id"
          sortedColumn={sortedColumn}
          setSortedColumn={setSortedColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          pagination
          activePage={activePage}
          totalPageCount={total?.totalPageCount}
          handleFetchData={handleClick}
        />
      </div>
      <p style={{ marginTop: "20px" }}>
        {t("pages.default.InvoiceColletarelReport.infoText")}
      </p>
    </>
  );
}
